/**
 * Game artwork, in Steam's sizes.
 *
 * A shelf only looks like a shelf when every tile is the same shape, so the sizes are fixed
 * here rather than left to whoever exports the art. They are Steam's own, because that is the
 * set every artist and every asset pack already targets: art made for a Steam page drops
 * straight in with no re-export.
 *
 * Both ends read this table. The uploader checks a file against it before spending an upload,
 * and the API checks it again before handing out a URL, so a dimension that never reached the
 * browser check still cannot land in a bucket.
 */

import { z } from 'zod';

export const ARTWORK_SLOTS = [
  'capsule',
  'header',
  'hero',
  'logo',
  'small',
  'background',
  'screenshot',
  'poster',
] as const;
export const artworkSlotSchema = z.enum(ARTWORK_SLOTS);
export type ArtworkSlot = z.infer<typeof artworkSlotSchema>;

/**
 * How a file is measured against its slot.
 *
 * `ratio` is the normal rule: the shape has to match, because the frame that draws it crops
 * anything else. `bounded` is for the logo, which has no shape of its own — it is whatever
 * the wordmark happens to be — so the only thing worth checking is that it is not larger
 * than the box it is drawn into.
 */
export type ArtworkFit = 'ratio' | 'bounded';

export interface ArtworkSpec {
  slot: ArtworkSlot;
  /** What Steam calls it, so an artist recognises the name. */
  label: string;
  /** The size to export at. Larger is allowed at the same ratio; smaller only warns. */
  width: number;
  height: number;
  fit: ArtworkFit;
  /** Where this one is drawn, in the words of the screen it appears on. */
  usage: string;
  /** True for the slot a game holds many of. Everything else is one image or none. */
  many?: boolean;
  /** True where the file has to carry transparency to be worth having. */
  wantsAlpha?: boolean;
}

export const GAME_ARTWORK: readonly ArtworkSpec[] = [
  {
    slot: 'capsule',
    label: 'Library capsule',
    width: 600,
    height: 900,
    fit: 'ratio',
    usage: 'The tile on the shelf and in the library.',
  },
  {
    slot: 'header',
    label: 'Header capsule',
    width: 920,
    height: 430,
    fit: 'ratio',
    usage: 'Wide rows and anywhere a tile is too tall to fit.',
  },
  {
    slot: 'hero',
    label: 'Library hero',
    width: 1920,
    height: 620,
    fit: 'ratio',
    usage: 'The featured banner on the home screen and the top of the game page.',
  },
  {
    slot: 'logo',
    label: 'Library logo',
    width: 1280,
    height: 720,
    fit: 'bounded',
    wantsAlpha: true,
    usage: 'The title itself, drawn over the hero instead of set in type.',
  },
  {
    slot: 'small',
    label: 'Small capsule',
    width: 462,
    height: 174,
    fit: 'ratio',
    usage: 'Search results and any row too short for a header capsule.',
  },
  {
    slot: 'background',
    label: 'Page background',
    width: 1920,
    height: 1080,
    fit: 'ratio',
    usage: 'Behind the whole app while the game is on screen, blurred back so text stays readable.',
  },
  {
    slot: 'screenshot',
    label: 'Screenshot',
    width: 1920,
    height: 1080,
    fit: 'ratio',
    many: true,
    usage: 'The gallery on the game page.',
  },
  {
    slot: 'poster',
    label: 'Trailer poster',
    width: 1920,
    height: 1080,
    fit: 'ratio',
    many: true,
    usage: 'The still on a trailer before it is played, and its thumbnail in the gallery.',
  },
];

export const ARTWORK_BY_SLOT: Record<ArtworkSlot, ArtworkSpec> = Object.fromEntries(
  GAME_ARTWORK.map((spec) => [spec.slot, spec]),
) as Record<ArtworkSlot, ArtworkSpec>;

/** The slots a game holds exactly one of, in the order the admin panel offers them. */
export const SINGLE_ARTWORK: readonly ArtworkSpec[] = GAME_ARTWORK.filter((s) => !s.many);

/** How many screenshots one game may carry. Past this the gallery stops being a gallery. */
export const MAX_SCREENSHOTS = 12;

/**
 * Where the logo sits on the hero, the way Steam anchors it.
 *
 * Two positions rather than Steam's four: the scrim on our hero darkens the left, so a logo
 * anywhere else is sitting on artwork it cannot be read against.
 */
export const LOGO_ANCHORS = ['bottom-left', 'center-left'] as const;
export const logoAnchorSchema = z.enum(LOGO_ANCHORS);
export type LogoAnchor = z.infer<typeof logoAnchorSchema>;

/** How wide the logo is drawn, as a percentage of the hero. Steam's own control. */
export const LOGO_WIDTH_MIN = 15;
export const LOGO_WIDTH_MAX = 70;
export const LOGO_WIDTH_DEFAULT = 38;

/**
 * How far off the ratio an image may be before it is refused.
 *
 * One percent is about a pixel of rounding on a 600 wide capsule, which is what an export at
 * an odd scale factor costs. Anything further is a crop someone did not mean to make, and it
 * would be cropped a second time by the tile that draws it.
 */
export const ARTWORK_RATIO_TOLERANCE = 0.01;

export interface ArtworkProblem {
  /** True when the image is refused. False is a warning: it uploads, it is just soft. */
  blocking: boolean;
  message: string;
}

/** `920 × 430`, with the multiplication sign rather than an x. */
export function formatArtworkSize(spec: ArtworkSpec): string {
  return `${spec.width} × ${spec.height}`;
}

/**
 * What is wrong with an image for this slot, or null when nothing is.
 *
 * The ratio is the blocking half: art at the wrong shape is cropped by the tile, and the crop
 * eats whatever the artist put at the edges. Size is only a warning, because a 460 wide
 * capsule still draws correctly, it just draws soft on a big screen.
 */
export function artworkProblem(
  slot: ArtworkSlot,
  width: number,
  height: number,
): ArtworkProblem | null {
  const spec = ARTWORK_BY_SLOT[slot];
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { blocking: true, message: 'That file is not an image this app can read.' };
  }

  // A logo is whatever shape the wordmark is, so there is no ratio to hold it to. The box is
  // the whole rule: past it the browser is scaling a picture down on every paint for nothing.
  if (spec.fit === 'bounded') {
    if (width > spec.width || height > spec.height) {
      return {
        blocking: true,
        message: `The ${spec.label.toLowerCase()} fits inside ${formatArtworkSize(spec)}. This one is ${width} × ${height}.`,
      };
    }
    return null;
  }

  const want = spec.width / spec.height;
  const got = width / height;
  if (Math.abs(got - want) / want > ARTWORK_RATIO_TOLERANCE) {
    return {
      blocking: true,
      message: `The ${spec.label.toLowerCase()} is ${formatArtworkSize(spec)} or the same shape. This one is ${width} × ${height}.`,
    };
  }

  if (width < spec.width || height < spec.height) {
    return {
      blocking: false,
      message: `This is smaller than ${formatArtworkSize(spec)}, so it will look soft on a large screen.`,
    };
  }

  return null;
}
